import {
  Component,
  Host,
  h,
  Element,
  Prop,
  Event,
  EventEmitter,
  Watch,
  State,
} from '@stencil/core';
import { Expand } from './types';
import classcat from 'classcat';

export interface AhaSidebar extends HTMLAhaSidebarElement {}

@Component({
  tag: 'aha-sidebar',
  styleUrl: 'aha-sidebar.css',
  shadow: true,
})
export class AhaSidebar {
  @Element() host!: HTMLAhaSidebarElement;

  private wrapperElem?: HTMLElement;
  private headerElem?: HTMLElement;

  // Whether the aha-sidebar is open or closed.
  @Prop({ reflect: true }) open?: boolean = false;
  @Watch('open')
  watchOpen() {
    if (!this.expands) return;

    // Enable animations after the firest render.
    this.paused = false;
  }

  // Whether the sidebar should float above the content.
  @Prop({ reflect: true }) float?: boolean = false;

  // Whether the sidebar's header should have a bottom border.
  @Prop({ reflect: true }) headerBorder?: boolean = false;

  // The direction the aha-sidebar should open in. If not set, the aha-sidebar will not be collapsible.
  @Prop({ reflect: true }) expands?: Expand;
  @Watch('expands')
  watchExpands() {
    if (!this.expands) this.open = true;
  }

  // Hide the label when the sidebar is open.
  @Prop({ reflect: true }) hideOpenedLabel?: boolean = false;

  // The width of the aha-settigs-panel. Can be a number (300) or any valid CSS width value (300px, 50%, etc).
  @Prop({ reflect: true }) width: string = '300';
  @Watch('width')
  watchWidth() {
    if (this.width) {
      if (/%/.test(this.width))
        throw new Error('Width of a <aha-sidebar> element cannot be a percentage.');
      // If the width is set to just a number (ex. 32 instead of 32px), convert it to a string an set the units as pixels.
      const convertedWidth = Number(this.width);
      this.host.style.setProperty(
        '--aha-sidebar--width',
        isNaN(convertedWidth) ? this.width : `${convertedWidth}px`,
      );
    } else {
      this.host.style.removeProperty('--aha-sidebar--width');
    }
  }

  // Wether or not the sidebar animation is paused. By default is set to true to prevent the sidebar from animating when it is first rendered.
  @State() paused: boolean = true;

  /**
   * Triggers when the sidebar is beginning to transition to an open state.
   */
  @Event({
    eventName: 'aha-sidebar:open',
    cancelable: true,
    bubbles: true,
  }) //eslint-disable-line @stencil/decorators-style
  openEvent!: EventEmitter;

  /**
   * Triggers when the sidebar is finished transitioning to an open state.
   */
  @Event({
    eventName: 'aha-sidebar:opened',
    cancelable: true,
    bubbles: true,
  }) //eslint-disable-line @stencil/decorators-style
  openedEvent!: EventEmitter;

  /**
   * Triggers when the sidebar is beginning to transition to an closed state.
   */
  @Event({
    eventName: 'aha-sidebar:close',
    cancelable: true,
    bubbles: true,
  }) //eslint-disable-line @stencil/decorators-style
  closeEvent!: EventEmitter;

  /**
   * Triggers when the sidebar is finished transitioning to an closed state.
   */
  @Event({
    eventName: 'aha-sidebar:closed',
    cancelable: true,
    bubbles: true,
  }) //eslint-disable-line @stencil/decorators-style
  closedEvent!: EventEmitter;

  // Listen for animation events on the wrapperElem rather than the host element, as the host element will not receive animation events for floated aha-sidebar elements, as the host element is not the element that is being animated.
  listenForAnimationEvents() {
    if (!this.wrapperElem) return;
    const inAnimations = ['slide-in', 'float-slide-in'];
    const outAnimations = ['slide-out', 'float-slide-out'];

    this.wrapperElem.addEventListener('animationstart', e => {
      if (outAnimations.includes(e.animationName)) {
        this.closeEvent.emit(this.host);
      } else if (inAnimations.includes(e.animationName)) {
        this.openEvent.emit(this.host);
      }
    });

    this.wrapperElem.addEventListener('animationend', e => {
      if (outAnimations.includes(e.animationName)) {
        this.closedEvent.emit(this.host);
      } else if (inAnimations.includes(e.animationName)) {
        this.openedEvent.emit(this.host);
      }
    });
  }

  connectedCallback() {
    this.watchExpands();
  }

  componentDidLoad() {
    this.watchWidth();
    this.listenForAnimationEvents();

    if (this.headerElem) {
      new ResizeObserver(entries => {
        entries.forEach(entry => {
          this.host.style.setProperty(
            '--aha-sidebar--collapsed-width',
            `${entry.borderBoxSize[0].blockSize}px`,
          );
        });
      }).observe(this.headerElem);
    }
  }

  render() {
    return (
      <Host paused={this.paused}>
        <div id="wrapper" part="wrapper" ref={el => (this.wrapperElem = el)}>
          <section>
            <header
              ref={el => (this.headerElem = el)}
              class={classcat({
                'with-border': this.headerBorder,
              })}
            >
              {this.expands && (
                <button
                  type="button"
                  onClick={() => {
                    this.open = !Boolean(this.open);
                  }}
                  id="close"
                >
                  <fa-icon
                    class={classcat({
                      'fa-regular': true,
                      'fa-angles-left': this.expands === 'left',
                      'fa-angles-right': this.expands === 'right',
                    })}
                  ></fa-icon>
                </button>
              )}
              <div id="label" part="label">
                <slot name="label"></slot>
              </div>
            </header>

            <div id="content">
              {/* The placeholder exists so that the "header" takes up as much vertical space in the non-open state as it does in the open state. */}
              <div id="header-placeholder"></div>

              <main>
                <slot></slot>
              </main>
            </div>
          </section>
        </div>
      </Host>
    );
  }
}
